import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Zap, Users, Calendar, Check, ArrowRight, Ticket, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import BoxSeatLogo from '../components/BoxSeatLogo';

const PERKS = [
  { icon: Calendar, text: 'Unlimited access to all events & venues' },
  { icon: Users, text: 'Create & join fan meetups' },
  { icon: Zap, text: 'QR code sharing & RSVP management' },
  { icon: Shield, text: 'Early access to new features' },
];

export default function SeasonPassPage() {
  const navigate = useNavigate();
  const { user, fetchCurrentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');

  // If already subscribed, redirect to dashboard
  useEffect(() => {
    if (user?.subscription_status === 'active' || user?.subscription_status === 'promo') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/subscription/checkout`, { origin_url: origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handlePromo = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setPromoSuccess('');
    try {
      const res = await axios.post(`${API}/subscription/apply-promo`, { code: promoCode });
      setPromoSuccess(res.data.message);
      await fetchCurrentUser();
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <BoxSeatLogo size="xs" onClick={() => navigate('/')} />
          <button
            onClick={() => { localStorage.removeItem('token'); delete axios.defaults.headers.common['Authorization']; navigate('/login'); }}
            className="text-sm text-zinc-400 hover:text-white transition-colors"
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16 px-6 md:px-12 max-w-4xl mx-auto">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider">
            <Ticket size={14} /> Season Pass
          </span>
        </div>

        {/* Heading */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] mb-4">
            Get Your <span className="text-primary">Season Pass</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-xl mx-auto">
            Unlock full access to Philadelphia's ultimate sports & entertainment platform.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto mb-12">
          <div data-testid="season-pass-card" className="bg-surface border-2 border-primary/30 rounded-2xl overflow-hidden shadow-[0_0_60px_-15px_rgba(255,183,3,0.15)]">
            {/* Price header */}
            <div className="bg-gradient-to-b from-primary/10 to-transparent p-8 text-center border-b border-white/5">
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="font-heading text-6xl font-black text-primary">$2.99</span>
                <span className="text-zinc-500 text-sm font-semibold">/month</span>
              </div>
              <p className="text-zinc-400 text-sm">Cancel anytime</p>
            </div>

            {/* Perks */}
            <div className="p-8 space-y-4">
              {PERKS.map((perk, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check size={16} className="text-primary" />
                  </div>
                  <span className="text-sm text-zinc-300">{perk.text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="px-8 pb-8">
              <button
                data-testid="subscribe-btn"
                onClick={handleCheckout}
                disabled={loading}
                className="w-full py-4 bg-primary text-black font-bold uppercase tracking-wide rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-all glow-amber flex items-center justify-center gap-2 text-base"
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> Processing...</>
                ) : (
                  <>Subscribe Now <ArrowRight size={18} /></>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Promo Code Section */}
        <div className="max-w-md mx-auto">
          <div className="bg-surface border border-white/10 rounded-xl p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-3 text-center">Have a promo code?</p>
            <form onSubmit={handlePromo} className="flex gap-3">
              <input
                data-testid="promo-code-input"
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter code"
                className="flex-1 px-4 py-3 bg-black/50 border border-white/20 text-white rounded-lg placeholder:text-zinc-600 focus:ring-2 focus:ring-primary focus:border-transparent uppercase tracking-wider text-sm font-bold"
              />
              <button
                data-testid="apply-promo-btn"
                type="submit"
                disabled={promoLoading || !promoCode.trim()}
                className="px-6 py-3 bg-white/10 text-white font-bold uppercase tracking-wide rounded-lg hover:bg-white/15 disabled:opacity-50 transition-all text-sm"
              >
                {promoLoading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
              </button>
            </form>
            {promoError && <p data-testid="promo-error" className="mt-3 text-sm text-accent text-center">{promoError}</p>}
            {promoSuccess && <p data-testid="promo-success" className="mt-3 text-sm text-secondary text-center">{promoSuccess}</p>}
          </div>
        </div>

        {/* Trust/Legal */}
        <p className="text-center text-xs text-zinc-600 mt-8 max-w-sm mx-auto">
          Secured by Stripe. By subscribing you agree to our{' '}
          <button onClick={() => navigate('/terms')} className="underline hover:text-zinc-400">Terms of Service</button>.
        </p>
      </main>
    </div>
  );
}
